"use client";

import { useState } from "react";

import { CardPhoto } from "@/components/ui/card-photo";
import FilterBar from "@/components/FilterBar";
import EducationModal from "@/components/EducationModal";

import { EducationItem } from "@/data/education";
import type { FilterGroup } from "@/data/filters";
import { defaultSortConfigs, sortItems } from "@/lib/sortUtils";
import { track } from "@/utils/tracking";

type EducationClientProps = {
  initialEducation: EducationItem[];
  educationFilters: FilterGroup[];
  locale: string;
};

export default function EducationClient({ 
  initialEducation, 
  educationFilters
}: EducationClientProps) {
  // Initialize state for selected item and modal open state
  const [selectedItem, setSelectedItem] = useState<EducationItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Initialize filtered items state
  const [filteredEducation, setFilteredEducation] = useState<EducationItem[]>(initialEducation);
  
  // Handle filter changes
  const handleFilterChange = (activeFilters: Record<string, string | null>) => {
    const hasActiveFilters = Object.values(activeFilters).some(filter => filter !== null);
    
    if (!hasActiveFilters) {
      // No filters active, show initial education
      setFilteredEducation(initialEducation);
    } else {
      // Filters active, show filtered content
      // Import education data dynamically for filtering
      import('@/data/education').then(({ default: education }) => {
        const filtered = education.filter(item => {
          return Object.values(activeFilters).every(activeFilter => {
            if (activeFilter === null) return true;
            return item.tags.includes(activeFilter);
          });
        });
        
        setFilteredEducation(sortItems(filtered, defaultSortConfigs.education));
      });
    }
  };

  // Handle card click
  const handleCardClick = (item: EducationItem) => {
    track('education_item_clicked', {
      title: item.name
    });
    setSelectedItem(item);
    setModalOpen(true);
  };

  return (
    <>
      {/* Only show filter bar if there are filter groups configured */}
      {educationFilters.length > 0 && (
        <FilterBar 
          filterGroups={educationFilters}
          onFilterChange={handleFilterChange}
          className="mb-6"
        />
      )}
      
      {/* Always render cards client-side with proper event handlers */}
      <ul className="grid-template-logos mt-6 mb-2">
        {filteredEducation.map((item, index) => (
          <li key={`${item.name}-${index}`}>
            <CardPhoto
              className="cursor-pointer"
              photo={item.image}
              photoDark={item.imageDark}
              alt={item.name}
              onClick={() => handleCardClick(item)}
            />
          </li>
        ))}
      </ul>

      {/* Modal */}
      {selectedItem && (
        <EducationModal 
          item={selectedItem} 
          itemIndex={filteredEducation.indexOf(selectedItem)}
          open={modalOpen} 
          onOpenChange={setModalOpen}
        />
      )}
    </>
  );
}